import {
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { Plus24Px, globalDateTimeFormat, useToast } from "@qubit/autoparts";
import dayjs from "dayjs";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm
} from "react-hook-form";

import { useAppSelector } from "~/app/store";
import { generateUuid, getRndInteger } from "~/lib/helpers";
import { groupBy } from "~/lib/shared";
import { useCreateCustomClientBOrderMutation } from "~/redux/customClientB/pickOrder.hooks";
import {
  CreateOrder,
  OrderLine,
  OrderLineTote,
  usePostOrdersCreateMutation
} from "~/redux/public/orders.openApi";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectUsersClientId } from "~/redux/selectors/authSelectors";
import {
  selectOrderCreationSelectedSku,
  selectOrderCreationSimplified
} from "~/redux/selectors/orderCreationSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";

import { FulfillmentCenterDto } from "~/types/api";

import { LineItem } from "./LineItem";
import { getErrorMessage, normalizeLineItemWeightThresholds } from "./shared";

import { translateToPickOrder } from "./translateToCustomClientBApi";

const createExternalOrderId = (): string =>
  `${dayjs().format("MMDD_hh:mm:ss")}_${getRndInteger(0, 99)}`;

const customerNamePool = [
  "Leonardo DiStackrio",
  "Anne Hathawarehouse",
  "Morgan Freightman",
  "Kim Cardbatchshian",
  "Wharry Potter",
  "Shelvin Diesel",
  "Scarlet Palletson",
  "Storage Clooney",
  "Jim Carry",
  "Jennifer Anistorage",
  "Mark Warehouseberg",
  "Putawayne The Rack Johnson",
  "Harrison Forklift",
  "Samuel L. Stackson",
  "Wilson Pallet",
  "Cardi Binventory",
  "Frank S'bin-atra",
  "Gwen Stackfani",
  "Freddie Merchandisury",
  "Johnny Cashier",
  "AutosTorey Spelling",
  "Orderlando Bloom",
  "Replenishmeryl Streep",
  "Cartney Cox",
  "Cycle Countney Love",
  "Holdie Hawn",
  "Gridley Cooper",
  "Pickolas Cage",
  "Bin Affleck",
  "Bulk Hogan",
  "Gridney Houston",
  "Tote Hanks",
  "Progress Barbra Streisand",
  "Robport Pattinson",
  "Natalie Portman",
  "Sam OutOf Stockwell",
  "Qubitney Spears",
  "Gridgory Peck",
  "Binona Ryder",
  "Pick Jagger",
  "Jon Bin Jovi",
  "Print Eastwood",
  "H'user Jackman",
  "Expiry Date Blanchett",
  "FulfillmentCenterId-ris Elba",
  "PTLState Middleton",
  "Expressica Tandy",
  "Decantino Tarantino",
  "Tempzoney Depp",
  "ConfiguReese Witherspoon",
  "Cart Simpson",
  "CommittedCount Dracula",
  "Robins Hood",
  "Eric Cartman",
  "Inventory Jones",
  "Disconnected Sheeran",
  "Binnie the Pooh",
  "Aristotel",
  "Pablo Pickasso",
  "George AndonBoarwell",
  "Rene Descartes",
  "Restockrates",
  "Charles Bulkowski",
  "Binjamin Franklin",
  "Jimmy Carter",
  "Cart Twain",
  "Bincent van Gogh",
  "Immanuel DeKant",
  "Printcent Price",
  "Tempzoneey Deschanel",
  "Aisle Pacino",
  "Scandra Bullock",
  "Totey Macguire",
  "Zoney Kravitz",
  "Batch Galifianakis",
  "Picktoria Jackson",
  "Sean Pending",
  "Adjustin Bieber",
  "Bruce Chillis"
];

const initialCustomerName =
  customerNamePool[Math.floor(Math.random() * customerNamePool.length)];

const createCreatedDate = (timezone: string): string =>
  dayjs().tz(timezone).toISOString();

const createInitialLineItem = (): OrderLine => ({
  sku: "",
  externalLineItemId: getRndInteger(1, 500).toString(),
  quantity: {
    unit: "ea",
    value: 1.0
  },
  instructions: "order instructions",
  acceptableSubstitutions: []
});

const createInitialCreateOrder = (
  fulfillmentCenter?: FulfillmentCenterDto
): CreateOrder => {
  const createdDate = createCreatedDate(fulfillmentCenter?.timeZone || "utc");

  return {
    id: generateUuid(),
    externalOrderId: createExternalOrderId(),
    fulfillmentCenterId: fulfillmentCenter?.fulfillmentCenterId || "",
    orderType: "Fresh",
    priority: "Standard",
    createdDate,
    customerName: `${initialCustomerName} ${dayjs().format("MMM D h:mm")}`,
    earliestPickingStartTime: createdDate,
    pickingEndTime: dayjs()
      .tz(fulfillmentCenter?.timeZone || "utc")
      .endOf("day")
      .toISOString(),
    lineItems: []
  };
};

const generateRandomToteId = (existingToteId?: number) =>
  getRndInteger(100000000, 999999999, existingToteId).toString();

const generateInitialTote = (
  isExternalTotes: boolean,
  existingToteId?: number
): OrderLineTote | undefined =>
  isExternalTotes
    ? {
        id: `${generateRandomToteId(existingToteId)}`,
        priority: undefined
      }
    : undefined;

export const PickTab = () => {
  const { successToast, errorToast } = useToast();
  const { dev_useV2OrderCreation } = useAppSelector(selectServerConfig);

  const clientId = useAppSelector(selectUsersClientId);
  const selectedSku = useAppSelector(selectOrderCreationSelectedSku);
  const simplified = useAppSelector(selectOrderCreationSimplified);
  const fulfillmentCenter =
    useAppSelector(selectUsersFulfillmentCenter) || undefined;

  const [createPickOrder, { isLoading: createPickOrderInProgress }] =
    useCreateCustomClientBOrderMutation();
  const [createOrder, { isLoading: createOrderInProgress }] =
    usePostOrdersCreateMutation();

  const isExternalTotes =
    fulfillmentCenter?.toteConfiguration === "ExternalTotes";

  const createOrderContext = useForm<CreateOrder>({
    defaultValues: createInitialCreateOrder(fulfillmentCenter)
  });

  const {
    fields: lineItemFields,
    insert: insertLineItem,
    append: appendLineItem,
    remove: removeLineItem
  } = useFieldArray({
    control: createOrderContext.control,
    name: "lineItems"
  });

  const handleCreateOrder = async (data: CreateOrder) => {
    if (!clientId) return;

    const order: CreateOrder = {
      ...data,
      lineItems: data.lineItems.map(normalizeLineItemWeightThresholds)
    };

    try {
      if (dev_useV2OrderCreation && fulfillmentCenter) {
        await createPickOrder({
          clientId,
          fulfillmentCenterId: fulfillmentCenter.fulfillmentCenterId,
          ...translateToPickOrder(order, fulfillmentCenter)
        }).unwrap();
      } else {
        await createOrder({ createOrder: order }).unwrap();
      }
      successToast("Successfully created order");
    } catch (err) {
      errorToast(getErrorMessage(err));
    }

    createOrderContext.setValue("id", generateUuid());
    createOrderContext.setValue("externalOrderId", createExternalOrderId());
    const nextCreatedDate = createCreatedDate(
      fulfillmentCenter?.timeZone || "utc"
    );
    createOrderContext.setValue("createdDate", nextCreatedDate);
    createOrderContext.setValue("earliestPickingStartTime", nextCreatedDate);

    if (isExternalTotes) {
      // need to ensure that totes are still grouped the same
      const newToteIds = [
        ...new Set(order.lineItems.map((li) => li.tote?.id))
      ].reduce<{ [index: string]: string }>((prev, curr) => {
        if (curr) {
          // eslint-disable-next-line no-param-reassign
          prev[curr] = generateRandomToteId(parseInt(curr, 10));
        }
        return prev;
      }, {});

      order.lineItems.forEach((li, i) => {
        if (li.tote) {
          createOrderContext.setValue(
            `lineItems.${i}.tote.id`,
            newToteIds[li.tote.id]
          );
        }
      });
    }
  };

  const handleAddLineItem = (index?: number, tote?: OrderLineTote) => {
    const lineItem = {
      ...createInitialLineItem(),
      sku: selectedSku || "",
      tote: tote ?? generateInitialTote(isExternalTotes)
    };
    if (index !== undefined) {
      insertLineItem(index, lineItem);
    } else {
      appendLineItem(lineItem);
    }
  };

  const groupedLineItems = Object.entries(
    groupBy(
      lineItemFields.map((field, index) => ({ ...field, index })),
      (field) => field.tote?.id || ""
    )
  );

  groupedLineItems.map(([_, group]) => group.sort((a, b) => a.index - b.index));

  return (
    <>
      <Typography variant="h6" sx={{ my: 2 }}>
        Pick Order
      </Typography>

      <FormProvider {...createOrderContext}>
        <form onSubmit={createOrderContext.handleSubmit(handleCreateOrder)}>
          <Stack flexWrap="wrap" gap={3}>
            <Stack direction="row" flexWrap="wrap" gap={2}>
              <TextField
                {...createOrderContext.register("customerName")}
                inputProps={{ sx: { width: 300 } }}
                variant="standard"
                label="Customer Name"
              />
              <TextField
                {...createOrderContext.register("orderType")}
                inputProps={{ sx: { width: 300 } }}
                variant="standard"
                label="Order Type"
              />
              {!simplified && (
                <>
                  <TextField
                    {...createOrderContext.register("externalOrderId")}
                    variant="standard"
                    label="External Order Id"
                  />
                  <TextField
                    {...createOrderContext.register("priority")}
                    variant="standard"
                    label="Priority"
                  />
                </>
              )}
            </Stack>
            {!simplified && (
              <Stack direction="row" flexWrap="wrap" gap={2}>
                <Controller
                  control={createOrderContext.control}
                  name="earliestPickingStartTime"
                  render={({
                    field: { onBlur, value, onChange, ...field }
                  }) => (
                    <DateTimePicker
                      {...field}
                      label="Earliest Picking Start Time"
                      value={value ? dayjs(value) : null}
                      onChange={(event) => {
                        onChange(event?.toISOString() || null);
                      }}
                      format={globalDateTimeFormat}
                      slotProps={{
                        textField: {
                          variant: "standard",
                          onBlur,
                          sx: {
                            ".MuiInputBase-input": {
                              minWidth: "180px"
                            }
                          }
                        }
                      }}
                    />
                  )}
                />
                <Controller
                  control={createOrderContext.control}
                  name="pickingEndTime"
                  render={({
                    field: { onBlur, value, onChange, ...field }
                  }) => (
                    <DateTimePicker
                      {...field}
                      label="Picking End Time"
                      value={value ? dayjs(value) : null}
                      onChange={(event) => {
                        onChange(event?.toISOString() || null);
                      }}
                      format={globalDateTimeFormat}
                      slotProps={{
                        textField: {
                          variant: "standard",
                          onBlur,
                          sx: {
                            ".MuiInputBase-input": {
                              minWidth: "180px"
                            }
                          }
                        }
                      }}
                    />
                  )}
                />
                <Controller
                  control={createOrderContext.control}
                  name="createdDate"
                  render={({
                    field: { onBlur, value, onChange, ...field }
                  }) => (
                    <DateTimePicker
                      {...field}
                      label="Created Date"
                      value={value ? dayjs(value) : null}
                      onChange={(event) => {
                        const createdDate = event?.toISOString() || null;
                        onChange(createdDate);
                        createOrderContext.setValue(
                          "earliestPickingStartTime",
                          createdDate
                        );
                      }}
                      format={globalDateTimeFormat}
                      slotProps={{
                        textField: {
                          variant: "standard",
                          onBlur,
                          sx: {
                            ".MuiInputBase-input": {
                              minWidth: "180px"
                            }
                          }
                        }
                      }}
                    />
                  )}
                />
              </Stack>
            )}
          </Stack>

          <Stack direction={"row"} my={2} gap={3}>
            <Button
              variant={
                createOrderContext.watch("lineItems").length
                  ? "contained"
                  : "subtle"
              }
              disabled={
                !createOrderContext.watch("lineItems").length ||
                createOrderInProgress ||
                createPickOrderInProgress
              }
              sx={{ width: 150 }}
              type="submit"
              color="primary"
            >
              {createOrderInProgress ? (
                <CircularProgress size={24} />
              ) : (
                "Create Order"
              )}
            </Button>
            <Button
              variant="subtle"
              disabled={!createOrderContext.watch("lineItems").length}
              onClick={() =>
                createOrderContext.reset(
                  createInitialCreateOrder(fulfillmentCenter)
                )
              }
            >
              Reset
            </Button>
          </Stack>

          {groupedLineItems
            .filter((group) => group[1].length)
            .map((group) => (
              <Paper key={group[0]} sx={{ p: 2, my: 2 }}>
                {isExternalTotes && (
                  <Typography>Tote Id: {group[0]}</Typography>
                )}
                {group[1].map((field) => (
                  <LineItem
                    key={field.id}
                    index={field.index}
                    removeLineItem={removeLineItem}
                  />
                ))}

                {isExternalTotes && (
                  <Button
                    variant="subtle"
                    disabled={!selectedSku}
                    startIcon={<Plus24Px fill="currentColor" />}
                    onClick={() =>
                      handleAddLineItem(
                        group[1].at(-1)?.index,
                        group[1][0].tote
                      )
                    }
                    aria-label={`Add Product to Tote ${group[1][0].tote?.id}`}
                  >
                    Product
                  </Button>
                )}
              </Paper>
            ))}

          <Paper sx={{ p: 2, my: 2 }}>
            {isExternalTotes && <Typography mb={2}>New Tote</Typography>}
            <Button
              variant={
                !createOrderContext.watch("lineItems").length
                  ? "contained"
                  : "subtle"
              }
              disabled={!selectedSku}
              startIcon={<Plus24Px fill="currentColor" />}
              onClick={() => handleAddLineItem()}
              aria-label="Add Product to New Tote"
            >
              Product
            </Button>
          </Paper>
        </form>
      </FormProvider>
    </>
  );
};
